
import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Text, Link, Spinner, HStack, PinInput, PinInputField } from "@chakra-ui/react";
import { useAuth } from "auth/authProvider";

const FIELD_W = "260px";
const pinFieldStyle = {
  bg: "transparent", border: "1px solid #fff", borderRadius: "10px", color: "#fff",
  fontSize: "16px", textAlign: "center", width: "36px", height: "42px",
  _focus: { borderColor: "#fff", boxShadow: "0 0 0 1px #fff" }
};

export default function EmailVerification({ email, onBack, onVerified }) {
  const { verifyEmail, resendVerificationCode } = useAuth();
  const isMountedRef = useRef(true);
  
  const [state, setState] = useState({
    code: "", error: "", isLoading: false, isResending: false, timer: 60, canResend: false
  });

  const updateState = (updates) => setState(prev => ({ ...prev, ...updates }));

  useEffect(() => () => { isMountedRef.current = false; }, []);

  useEffect(() => {
    if (state.timer > 0 && !state.canResend) {
      const interval = setInterval(() => updateState({ timer: state.timer - 1 }), 1000);
      return () => clearInterval(interval);
    } else if (state.timer === 0) {
      updateState({ canResend: true });
    }
  }, [state.timer, state.canResend]);

  const handleSubmit = async e => {
    e.preventDefault();
    updateState({ error: "" });
    
    if (state.code.length !== 6) {
      if (isMountedRef.current) updateState({ error: "Please enter the complete 6-digit verification code." });
      return;
    }

    if (isMountedRef.current) updateState({ isLoading: true });
    
    try {
      await verifyEmail(email, state.code);
      onVerified();
    } catch (err) {
      if (isMountedRef.current) updateState({ error: err.message || "Verification failed. Please try again." });
    } finally {
      if (isMountedRef.current) updateState({ isLoading: false });
    }
  };

  const handleResend = async () => {
    updateState({ error: "", isResending: true });
    try {
      await resendVerificationCode(email);
      updateState({ timer: 60, canResend: false });
    } catch (err) {
      updateState({ error: err.message || "Failed to resend code. Please try again." });
    } finally {
      updateState({ isResending: false });
    }
  };

  const handleCodeChange = (value) => updateState({ code: value, error: "" });

  return (
    <Box as="form" onSubmit={handleSubmit} display="flex" flexDirection="column" alignItems="center" px="25px">
      <Text color="#fff" fontSize="26px" mb="12px">Verify Email</Text>
      <Text color="#ADA8C3" fontSize="12px" mb="8px" textAlign="center">
        A verification code has been sent to
      </Text>
      <Text color="#fff" fontSize="12px" mb="28px" textAlign="center" fontWeight="bold">{email}</Text>

      <Box mb="20px" w={FIELD_W}>
        <HStack spacing="6px" justify="center">
          <PinInput value={state.code} onChange={handleCodeChange} placeholder="" size="lg" focusBorderColor="#fff">
            {[...Array(6)].map((_, i) => (
              <PinInputField key={i} {...pinFieldStyle} />
            ))}
          </PinInput>
        </HStack>
      </Box>

      <Text color="#ADA8C3" fontSize="10px" mb="20px" textAlign="center">
        Enter the 6-digit verification code
      </Text>

      {state.error && (
        <Text color="red" fontSize="12px" mb="15px" textAlign="center" fontWeight="bold">
          {state.error}
        </Text>
      )}

      {state.isLoading ? (
        <Spinner color="white" mt="15px" w="30px" h="30px" thickness="4px" speed="0.65s" />
      ) : (
        <Button type="submit" w={FIELD_W} mt="15px" p="10px" bg="#fff" color="#000" borderRadius="10px"
          _hover={{ cursor: "pointer", bg: "transparent", color: "#fff", outline: "1px solid #fff" }}>
          Verify
        </Button>
      )}

      <Box mt="20px" textAlign="center">
        {state.canResend ? (
          <Text fontSize="12px" color="#fff">
            Didn't receive the code?{" "}
            {state.isResending ? (
              <Text as="span" color="#ADA8C3">Sending...</Text>
            ) : (
              <Link color="#fff" fontWeight="bold" cursor="pointer" onClick={handleResend}
                _hover={{ textDecoration: "underline" }}>
                Resend Code
              </Link>
            )}
          </Text>
        ) : (
          <Text fontSize="12px" color="#ADA8C3">Resend code in {state.timer}s</Text>
        )}
      </Box>

      <Text fontSize="12px" color="#fff" mt="15px" textAlign="center">
        <Link href="#" fontWeight="bold" color="#fff" onClick={e => { e.preventDefault(); onBack(); }}>
          ← Back
        </Link>
      </Text>
    </Box>
  );
}